import React from "react";
import { ParticipantDetailContextProviderType, ParticipantDetailContextType } from "./type";
import { isEmpty } from "../../../../shared/utils";
import { useAuth } from "../../../../shared/context/auth";
import {
  getEvent,
  getEventByProgramOrganizationId,
  getEventDetailsByEventParticipantId,
  getProgram,
  getProgramAndEventName,
  getProgramByProgramOrganizationId
} from "./util";
import { ApiProgramLiteForDashboard } from "shared/types/ApiProgramLiteForDashboard";
import { ProgramType } from "shared/enums/ProgramType";
import { useGetParticipantDetails } from "./dataloader";
const ParticipantDetailContext = React.createContext<ParticipantDetailContextType>({} as ParticipantDetailContextType);
function ParticipantDetailContextProvider(props: ParticipantDetailContextProviderType) {
  const { userDetail, isLoggedIn } = useAuth();
  const [screenName, setScreenName] = React.useState<string>();
  const { data: participantDetails, refetch: refreshParticipantDetails } = useGetParticipantDetails(userDetail?.id);

  const getProgramDetails = React.useCallback(
    (programParticipantId: string) => {
      return getProgram(programParticipantId, participantDetails);
    },
    [participantDetails]
  );

  const getProgramDetailsByProgramOrganizationId = React.useCallback(
    (programOrganizationId: string) => {
      return getProgramByProgramOrganizationId(programOrganizationId, participantDetails);
    },
    [participantDetails]
  );

  const getEventDetailsByProgramOrganizationId = React.useCallback(
    (programOrganizationId: string) => {
      return getEventByProgramOrganizationId(programOrganizationId, participantDetails);
    },
    [participantDetails]
  );

  const getEventDetails = React.useCallback(
    (programParticipantId: string) => {
      return getEvent(programParticipantId, participantDetails);
    },
    [participantDetails]
  );

  const getProgramAndEventNameDetails = React.useCallback(
    (programParticipantId: string) => {
      return getProgramAndEventName(programParticipantId, participantDetails);
    },
    [participantDetails]
  );

  const getEventDetailsByEventParticipant = React.useCallback(
    (eventParticipantId: string) => {
      return getEventDetailsByEventParticipantId(eventParticipantId, participantDetails);
    },
    [participantDetails]
  );

  const getProgramDetailsByEventParticipantIdAndProgramType = React.useCallback(
    (eventParticipantId: string, programType: ProgramType) => {
      return getEventDetailsByEventParticipant(eventParticipantId)?.programs?.find(
        (program: ApiProgramLiteForDashboard) => program.programType === programType
      );
    },
    [getEventDetailsByEventParticipant]
  );

  const getProgramDetailsByEventAndProgramId = React.useCallback(
    (eventId, programId): ApiProgramLiteForDashboard | undefined => {
      return participantDetails?.events?.find(e => e.id === eventId)?.programs?.find(p => p.id === programId);
    },
    [participantDetails?.events]
  );

  const refreshIfActionPendingInProgram = React.useCallback(
    (programId?: string) => {
      participantDetails?.events?.forEach(event => {
        let allProgramActionsCount = 0;
        if (programId) {
          allProgramActionsCount += event.programs?.find(p => p.id === programId)?.actions?.length ?? 0;
        } else {
          event.programs?.forEach(program => {
            allProgramActionsCount += program.actions?.length ?? 0;
          });
        }
        if (allProgramActionsCount > 0) {
          refreshParticipantDetails();
        }
      });
    },
    [refreshParticipantDetails, participantDetails?.events]
  );

  const refreshIfActionPendingInEvent = React.useCallback(() => {
    const actionCount =
      participantDetails?.events?.reduce((totalActionCount, event) => {
        return totalActionCount + (event.actions?.length ?? 0);
      }, 0) ?? 0;
    if (actionCount > 0) {
      refreshParticipantDetails();
    }
  }, [refreshParticipantDetails, participantDetails?.events]);

  const providerData: ParticipantDetailContextType = React.useMemo(() => {
    return {
      participantDetails,
      refreshParticipantDetails,
      getProgramAndEventNameDetails,
      getEventDetailsByEventParticipant,
      screenName,
      setScreenName,
      getProgramDetails,
      getEventDetails,
      getProgramDetailsByProgramOrganizationId,
      refreshIfActionPendingInProgram,
      getProgramDetailsByEventParticipantIdAndProgramType,
      getEventDetailsByProgramOrganizationId,
      getProgramDetailsByEventAndProgramId,
      refreshIfActionPendingInEvent
    };
  }, [
    participantDetails,
    refreshParticipantDetails,
    getProgramAndEventNameDetails,
    getEventDetailsByEventParticipant,
    screenName,
    getProgramDetails,
    getEventDetails,
    getProgramDetailsByProgramOrganizationId,
    refreshIfActionPendingInProgram,
    getProgramDetailsByEventParticipantIdAndProgramType,
    getEventDetailsByProgramOrganizationId,
    getProgramDetailsByEventAndProgramId,
    refreshIfActionPendingInEvent
  ]);

  if (!participantDetails && isLoggedIn()) {
    return null;
  }

  return <ParticipantDetailContext.Provider value={providerData}>{props.children}</ParticipantDetailContext.Provider>;
}

function useParticipantDetails() {
  const context = React.useContext(ParticipantDetailContext);
  if (isEmpty(context)) {
    throw new Error(`useParticipantDetails must be used within a ParticipantDetailContextProvider`);
  }
  return context;
}

export { ParticipantDetailContextProvider, useParticipantDetails };
